//-- Copyright (c) 2021 TMT Insights Group, LLC
import { globalBaseTheme, darkBaseTheme, lightBaseTheme, colors } from './baseThemes';
import { AssetType, CommonConstants } from '@/redux/slices/slice_types';

export const rootElementId = 'root';

export enum TitleTypes {
  feature = 'Feature',
  series = 'Series',
  season = 'Season',
  episode = 'Episode',
  trailer = 'Trailer',
  ova = 'OVA',
  movie = 'Movie',
  version = 'Version',
  campaign = 'Campaign',
  marketing = 'Marketing',
  sizzle = 'Sizzle'
}

export const TitleMap = {
  feature: 'feature',
  movie: 'feature',
  series: 'series',
  episode: 'episode',
  season: 'season',
  program: 'episode',
  version: 'version',
  trailer: 'trailer',
  special: 'trailer',
  marketing: 'marketing',
  sizzle: 'sizzle'
} as const;

export const tmdb_url = 'https://api.themoviedb.org/3';

const TMDB_API_KEY = '0cb789b39d52ced8709618c424d8f8a9';

export const tmdb_multi_search = (searchQuery: string, entity_type: string, year?: number | string) => {
  const searchType = entity_type === 'feature' ? 'movie' : 'multi';
  let params = `&language=en-US&query=${encodeURI(searchQuery)}&page=1&include_adult=false`;
  if (entity_type === 'feature' && year) {
    params += `&year=${year}`;
  }
  return `${tmdb_url}/search/${searchType}?api_key=${TMDB_API_KEY}${params}`;
};

export enum LoadingStatuses {
  pending = 'pending',
  rejected = 'rejected',
  fulfilled = 'fulfilled'
}

enum DisplayStatus {
  default = 'default',
  success = 'success',
  warning = 'warning',
  info = 'info',
  error = 'error'
}

export enum StatusColors {
  accepted = DisplayStatus.success,
  nothing = DisplayStatus.default,
  delivered = DisplayStatus.success,
  found = DisplayStatus.warning,
  complete = DisplayStatus.success,
  completed = DisplayStatus.success,
  research = DisplayStatus.default,
  failed_ingesting = DisplayStatus.error,
  not_available = DisplayStatus.info,
  created = DisplayStatus.default,
  failed = DisplayStatus.error,
  working = DisplayStatus.info,
  in_error = DisplayStatus.error,
  rejected = DisplayStatus.error,
  needs_attention = DisplayStatus.error,
  not_ready = DisplayStatus.default,
  unknown = DisplayStatus.default,
  resolved = DisplayStatus.success,
  ready = DisplayStatus.success,
  draft = DisplayStatus.default,
  deleted = DisplayStatus.default,
  cancelling = DisplayStatus.default,
  cancelled = DisplayStatus.default,
  skipped = DisplayStatus.default,
  referred = DisplayStatus.success,
  deferred = DisplayStatus.success,
  moving_to_staging = DisplayStatus.warning
}

export const PAGE_SIZE = 25;

export { globalBaseTheme, darkBaseTheme, lightBaseTheme, colors };

export enum Roles {
  read_all = 'read_all',
  read_dashboard = 'read_dashboard',
  view_dashboard = 'view_dashboard',
  read_orders = 'read_orders',
  write_orders = 'write_orders',
  read_jobs = 'read_jobs',
  read_tasks = 'read_tasks',
  view_tasks = 'view_tasks',
  read_content_assembly_task = 'read_content_assembly_task',
  read_order_job_assets = 'read_order_job_assets',
  write_order_job_assets = 'write_order_job_assets',
  view_filemanager = 'view_filemanager',
  read_filemanager = 'read_filemanager',
  read_profiles = 'read_profiles',
  write_profiles = 'write_profiles',
  view_titles = 'inventory.can_view_titles',
  read_titles = 'inventory.can_read_titles',
  view_fileingest = 'inventory.can_view_file_ingest',
  read_fileingest = 'inventory.can_read_file_ingest',
  view_orders = 'view_orders',
  view_jobs = 'view_jobs',
  view_orderjob = 'view_orderjob',
  view_orderjobtask = 'view_orderjobtask',
  view_order_job_assets = 'view_order_job_assets',
  view_titleentity = 'view_titleentity',
  read_events = 'monitoring.read_events',
  view_event = 'view_event',
  change_event = 'change_event',
  read_avail = 'read_avail',
  write_avail = 'write_avail',
  view_avail = 'focus.can_view_avails',
  view_releases = 'focus.can_view_calendar',
  view_schedule = 'focus.can_view_scheduling'
}

export type FileFormat =
  | 'aac'
  | 'ass'
  | 'avi'
  | 'bmp'
  | 'csv'
  | 'dfxp'
  | 'doc'
  | 'docx'
  | 'gif'
  | 'itt'
  | 'jpeg'
  | 'jpg'
  | 'json'
  | 'md'
  | 'mov'
  | 'mp3'
  | 'mp4'
  | 'mpeg'
  | 'mpg'
  | 'mxf'
  | 'pdf'
  | 'png'
  | 'prproj'
  | 'psd'
  | 'scc'
  | 'srt'
  | 'ssa'
  | 'stl'
  | 'sub'
  | 'svg'
  | 'tt'
  | 'tif'
  | 'tiff'
  | 'ttml'
  | 'txt'
  | 'ttext'
  | 'vtt'
  | 'wav'
  | 'webvtt'
  | 'xls'
  | 'xlsx'
  | 'xml'
  | '_scc';

export const SUPPORTED_SUBTITLE_AND_OTHER_PREVIEW_FORMATS: FileFormat[] = ['pdf', 'xml', 'txt', 'csv', 'json', 'itt', 'scc', 'srt', 'vtt'];
export const UNSUPPORTED_PREVIEW_FORMATS: FileFormat[] = ['mxf', 'dfxp'];

export type FileTypeGrouping = {
  [key in AssetType]: FileFormat[];
};

export const DEFAULT_COMMON_CONSTANTS: CommonConstants = {
  fileTypeGrouping: {
    video: ['mov', 'mxf', 'mp4', 'avi', 'mpg', 'mpeg'],
    audio: ['wav', 'aac', 'mp3'],
    subtitle: ['srt', 'scc', 'vtt', 'webvtt', 'sub', 'ass', 'ssa', 'ttml', 'dfxp', 'itt', 'stl'],
    image: ['jpg', 'jpeg', 'bmp', 'png', 'gif', 'svg', 'tiff', 'tif'],
    other: ['pdf', 'prproj', 'xml', 'csv', 'xls', 'xlsx', 'txt', 'docx', 'doc'],
    captions: []
  }
};

export type FileTypes = {
  [key in FileFormat]?: AssetType;
};

export enum ViewTypes {
  grid = 'grid',
  list = 'list'
}

export const FLAG_ICONS = {
  en: '🇬🇧',
  'en-us': '🇺🇸',
  'en-uk': '🇬🇧',
  nl: '🇳🇱',
  'nl-nl': '🇳🇱',
  fr: '🇫🇷',
  'fr-fr': '🇫🇷',
  de: '🇩🇪',
  'de-de': '🇩🇪',
  es: '🇪🇸',
  'es-es': '🇪🇸',
  it: '🇮🇹',
  'it-it': '🇮🇹',
  pt: '🇵🇹',
  'pt-pt': '🇵🇹',
  'pt-br': '🇧🇷',
  'zh-cn': '🇨🇳',
  'zh-tw': '🇹🇼',
  ja: '🇯🇵',
  'ja-jp': '🇯🇵',
  ko: '🇰🇷',
  'ko-kr': '🇰🇷',
  ar: '🇸🇦',
  'ar-sa': '🇸🇦',
  ru: '🇷🇺',
  'ru-ru': '🇷🇺',
  tr: '🇹🇷',
  'tr-tr': '🇹🇷',
  pl: '🇵🇱',
  'pl-pl': '🇵🇱',
  sv: '🇸🇪',
  'sv-se': '🇸🇪',
  no: '🇳🇴',
  'no-no': '🇳🇴',
  fi: '🇫🇮',
  'fi-fi': '🇫🇮',
  da: '🇩🇰',
  'da-dk': '🇩🇰',
  el: '🇬🇷',
  'el-gr': '🇬🇷',
  he: '🇮🇱',
  'he-il': '🇮🇱',
  'cs-cz': '🇨🇿',
  'hu-hu': '🇭🇺',
  'ro-ro': '🇷🇴',
  'sk-sk': '🇸🇰',
  'sl-sl': '🇸🇮'
};

export const PageSizeArr = [
  { key: 'page.size.10', value: 10 },
  { key: 'page.size.25', value: 25 },
  { key: 'page.size.50', value: 50 },
  { key: 'page.size.100', value: 100 }
];

export const PAGINATION_RESULTS_LABEL: Record<string, string> = {
  orders: 'orders',
  jobs: 'jobs',
  titleDetailJobs: 'jobs',
  relatedFiles: 'files',
  tasks: 'tasks',
  jobDetailTasks: 'tasks',
  processingTasks: 'processing exceptions',
  pendingAssets: 'pending assets',
  titles: 'titles',
  fileIngest: 'files',
  transferQueue: 'transfers',
  profiles: 'profiles'
};

export const TIMEZONE_MAP: Record<string, string> = {
  'PST/PDT': 'America/Los_Angeles',
  'MST/MDT': 'America/Denver',
  'CST/CDT': 'America/Chicago',
  'EST/EDT': 'America/New_York',
  'AKST/AKDT': 'America/Anchorage',
  'HST/HDT': 'Pacific/Honolulu',
  WET: 'Europe/Lisbon',
  GMT: 'Etc/GMT',
  UTC: 'Etc/UTC',
  JST: 'Asia/Tokyo',
  CET: 'Europe/Paris',
  EET: 'Europe/Athens',
  IST: 'Asia/Kolkata',
  AEST: 'Australia/Sydney',
  NZST: 'Pacific/Auckland'
};

export const NAVBAR_HEIGHT = 60;
export const NAVBAR_HIDE_TRANSITION_DURATION = 300;

export const FOCUS_CALENDAR_MONTHLY_VIEW_ITEMS_PER_DAY = 4;
export const FOCUS_CALENDAR_WEEKLY_VIEW_ITEMS_PER_DAY = 10;
